import React, { useMemo } from 'react';

import PropTypes from 'prop-types';

import { displayDate, displayTime } from '@adsk/alloy-react-datetime';
import {
  stylePropType,
  StylableComponent,
  useLocale,
} from '@adsk/alloy-react-helpers';
import theme from '@adsk/alloy-react-theme';
import { OverflowTooltip } from '@adsk/alloy-react-tooltip';

export type FooterProps = StylableComponent<HTMLDivElement> & {
  user?: { name: string };
  date?: Date;
};

const Footer = React.forwardRef<HTMLDivElement, FooterProps>(
  ({ user, date, className, style, ...props }, ref) => {
    const { T, locale } = useLocale(({ locale }) =>
      require(`../i18n/${locale}.json`)
    );
    const text = useMemo(() => {
      const dateString = date
        ? `${T.translate('DATE_PREFIX')} ${displayDate(date, {
            locale,
            format: 'numeric',
            utc: false,
          })}`
        : '';
      const timeString = date
        ? `${T.translate('TIME_PREFIX')} ${displayTime(date, {
            locale,
            format: 'numeric',
            utc: false,
          })}`
        : '';

      return `${dateString} ${timeString}`;
    }, [date, locale, T]);
    return (
      <div
        ref={ref}
        className={className}
        css={[
          {
            color: theme.colors.charcoal600,
            display: 'flex',
          },
          theme.typography.caption,
          style,
        ]}
        {...props}
      >
        {user?.name ? (
          <>
            <OverflowTooltip
              getTargetProps={() => ({ style: { overflow: 'auto' } })}
            >
              {user?.name}
            </OverflowTooltip>
            <span
              style={{
                whiteSpace: 'pre',
              }}
            >
              {' '}
            </span>
          </>
        ) : (
          ''
        )}
        <div style={{ flexShrink: 0 }}>{text}</div>
      </div>
    );
  }
);

Footer.displayName = 'Footer';

Footer.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** The date to show the activity for */
  date: PropTypes.instanceOf(Date),
  /** The user to show the activity for */
  user: PropTypes.shape({
    name: PropTypes.string.isRequired,
  }),
};

export default Footer;
